<script setup>
import {Head, useForm} from "@inertiajs/inertia-vue3";
import {ref} from "vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import Banner from "@/Components/Banner.vue";
import InputLabel from "@/Components/InputLabel.vue";
import InputError from "@/Components/InputError.vue";
import TextInput from "@/Components/TextInput.vue";
import SelectInput from "@/Components/SelectInput.vue";
import PhoneInput from "@/Components/PhoneInput.vue";
import CompanyLicenseDocumentField from "@/Components/Admin/Company/CompanyLicenseDocumentField.vue";
import {useTenantTextRules} from "@/helpers";

const props = defineProps({
  company: {type: Object, required: true},
  postActionUrl: {type: String, required: true},
  accountManagerName: {type: String, default: ""},
  licenseDocument: {type: Object, default: null},
});

const {resolveTextRule} = useTenantTextRules();

const form = useForm({
  contact: props.company.contact,
  phone: props.company.phone,
  fax: props.company.fax,
  address: props.company.address,
  address2: props.company.address2,
  city: props.company.city,
  state: props.company.state,
  zip: props.company.zip,
  country: props.company.country,
  www: props.company.www,
  intranet: props.company.intranet,
  trucking_service: props.company.trucking_service,
  insurance_carrier_policy_number: props.company.insurance_carrier_policy_number,
  notes: props.company.notes,
  has_notifications: (typeof props.company.has_notifications === 'undefined')
    ? true
    : props.company.has_notifications,
  license_document: null,
  remove_license_document: false,
});

const markedLicenseForRemoval = ref(false);

const onLicenseFileSelected = (file) => {
  form.license_document = file;
  form.remove_license_document = false;
  markedLicenseForRemoval.value = false;
};

const onLicenseRemove = () => {
  form.license_document = null;
  form.remove_license_document = true;
  markedLicenseForRemoval.value = true;
};

const onLicenseUndoRemove = () => {
  form.license_document = null;
  form.remove_license_document = false;
  markedLicenseForRemoval.value = false;
};

const save = () => {
  const options = {
    preserveScroll: true,
  };

  if (form.license_document) {
    options.forceFormData = true;
  }

  form.post(props.postActionUrl, options);
};

const addressLabel = () =>
  resolveTextRule('company.form.address', 'Address', 'public.company_update.form.address');

const address2Label = () =>
  resolveTextRule('company.form.address2', 'Address 2', 'public.company_update.form.address2');

const stateLabel = () =>
  resolveTextRule('company.form.state', 'State', 'public.company_update.form.state');

const truckingServiceLabel = () =>
  resolveTextRule(
    'company.form.trucking_service',
    'Trucking Service (If applicable)',
    'public.company_update.form.trucking_service'
  );

const insuranceCarrierPolicyNumberLabel = () =>
  resolveTextRule(
    'company.form.insurance_carrier_policy_number',
    'Insurance Carrier and Policy #',
    'public.company_update.form.insurance_carrier_policy_number'
  );
</script>

<template>
  <div>
    <Head title="Complete Company Information" />

    <div class="min-h-screen bg-gray-100 py-4 px-3 sm:py-6 sm:px-6">
      <div class="max-w-2xl mx-auto bg-white shadow-md rounded-lg p-4 sm:p-6 space-y-6 overflow-hidden">
        <div>
          <h1 class="text-xl font-semibold text-gray-900">Complete Company Information</h1>
          <p class="mt-1 text-sm text-gray-600">
            Review the locked fields and complete the remaining company details.
          </p>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-gray-200 pt-4 [&>*]:min-w-0">
          <div>
            <InputLabel value="Company Name" />
            <TextInput :model-value="company.client" type="text" class="mt-1 block w-full bg-gray-100" disabled />
          </div>
          <div>
            <InputLabel value="Company ID" />
            <TextInput :model-value="company.clientID" type="text" class="mt-1 block w-full bg-gray-100" disabled />
          </div>
          <div>
            <InputLabel value="Email" />
            <TextInput :model-value="company.email" type="email" class="mt-1 block w-full bg-gray-100" disabled />
          </div>
          <div>
            <InputLabel value="Account Manager" />
            <TextInput
              :model-value="accountManagerName || company.accountmanager"
              type="text"
              class="mt-1 block w-full bg-gray-100"
              disabled
            />
          </div>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-gray-200 pt-4 [&>*]:min-w-0">
          <div>
            <InputLabel for="contact" value="Contact" :required="true" />
            <TextInput id="contact" v-model="form.contact" type="text" class="mt-1 block w-full" />
            <InputError :message="form.errors.contact" class="mt-2" />
          </div>

          <div class="sm:col-span-2 min-w-0">
            <PhoneInput v-model="form.phone" />
            <InputError :message="form.errors.phone" class="mt-2" />
          </div>

          <div>
            <InputLabel for="www" value="URL" />
            <TextInput id="www" v-model="form.www" type="text" class="mt-1 block w-full" />
            <InputError :message="form.errors.www" class="mt-2" />
          </div>

          <div>
            <InputLabel for="fax" value="Fax" />
            <TextInput id="fax" v-model="form.fax" type="text" class="mt-1 block w-full" />
            <InputError :message="form.errors.fax" class="mt-2" />
          </div>

          <div>
            <InputLabel for="address" :value="addressLabel()" :required="true" />
            <TextInput id="address" v-model="form.address" type="text" class="mt-1 block w-full" />
            <InputError :message="form.errors.address" class="mt-2" />
          </div>

          <div>
            <InputLabel for="address2" :value="address2Label()" />
            <TextInput id="address2" v-model="form.address2" type="text" class="mt-1 block w-full" />
            <InputError :message="form.errors.address2" class="mt-2" />
          </div>

          <div>
            <InputLabel for="city" value="City" />
            <TextInput id="city" v-model="form.city" type="text" class="mt-1 block w-full" />
            <InputError :message="form.errors.city" class="mt-2" />
          </div>

          <div>
            <InputLabel for="state" :value="stateLabel()" />
            <TextInput id="state" v-model="form.state" type="text" class="mt-1 block w-full" />
            <InputError :message="form.errors.state" class="mt-2" />
          </div>

          <div>
            <InputLabel for="zip" value="Zip" />
            <TextInput id="zip" v-model="form.zip" type="text" class="mt-1 block w-full" />
            <InputError :message="form.errors.zip" class="mt-2" />
          </div>

          <div>
            <InputLabel for="country" value="Country" />
            <TextInput id="country" v-model="form.country" type="text" class="mt-1 block w-full" />
            <InputError :message="form.errors.country" class="mt-2" />
          </div>

          <div>
            <InputLabel for="intranet" value="Intranet URL" />
            <TextInput id="intranet" v-model="form.intranet" type="text" class="mt-1 block w-full" />
            <InputError :message="form.errors.intranet" class="mt-2" />
          </div>

          <div>
            <InputLabel for="trucking_service" :value="truckingServiceLabel()" />
            <TextInput id="trucking_service" v-model="form.trucking_service" type="text" class="mt-1 block w-full" />
            <InputError :message="form.errors.trucking_service" class="mt-2" />
          </div>

          <div>
            <InputLabel for="insurance_carrier_policy_number" :value="insuranceCarrierPolicyNumberLabel()" />
            <TextInput
              id="insurance_carrier_policy_number"
              v-model="form.insurance_carrier_policy_number"
              type="text"
              class="mt-1 block w-full"
            />
            <InputError :message="form.errors.insurance_carrier_policy_number" class="mt-2" />
          </div>

          <div>
            <InputLabel for="has_notifications" value="Email Notifications" />
            <SelectInput id="has_notifications" v-model="form.has_notifications" name="has_notifications" class="mt-1">
              <option :value="true">On</option>
              <option :value="false">Off</option>
            </SelectInput>
            <InputError :message="form.errors.has_notifications" class="mt-2" />
          </div>

          <CompanyLicenseDocumentField
            :existing-document="licenseDocument"
            :marked-for-removal="markedLicenseForRemoval"
            :license-document-error="form.errors.license_document"
            :remove-license-document-error="form.errors.remove_license_document"
            @file-selected="onLicenseFileSelected"
            @remove="onLicenseRemove"
            @undo-remove="onLicenseUndoRemove"
          />

          <div class="sm:col-span-2">
            <InputLabel for="notes" value="Notes" />
            <textarea
              id="notes"
              v-model="form.notes"
              rows="4"
              class="border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-sm mt-1 block w-full"
            ></textarea>
            <InputError :message="form.errors.notes" class="mt-2" />
          </div>
        </div>

        <div class="flex justify-stretch sm:justify-end border-t border-gray-200 pt-4">
          <PrimaryButton type="button" class="w-full sm:w-auto justify-center" :disabled="form.processing" @click="save">
            Save
          </PrimaryButton>
        </div>
      </div>
    </div>

    <div class="max-w-3xl sticky bottom-0 z-[100] mx-auto w-full px-2">
      <Banner />
    </div>
  </div>
</template>
